import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteRenewal,
  fetchMembershipRenewals,
  fetchRenewalById,
  verifyRenewal,
} from "../../../api/membershipRenewalsApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryApprovalsSection,
} from "../../../../../global/directory";
import GlobalFeedback from "../../../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";
import { GlobalModal } from "../../../../../global/GlobalModal";
import "../../../../user-builder/styles/userBuilder.css";
import "../../../../user-builder/styles/userBuilderTable.css";
import "../../../../user-builder/styles/userBuilderForms.css";
import "../../../styles/MembershipApplications.css";

const PAGE_SIZE = 10;
const COLUMN_VISIBILITY_KEY = "girinestham.membershipRenewals.columnVisibility";
const defaultUiFilters = () => ({
  student_search: "",
  column: "",
  value: "",
});

const COLUMN_DEFS = [
  { id: "select", label: "Select", defaultVisible: true, isAction: true },
  { id: "renewal_code", label: "Renewal Code", defaultVisible: true },
  { id: "student_name", label: "Student", defaultVisible: true },
  { id: "plan_name", label: "Plan", defaultVisible: true },
  { id: "amount", label: "Amount", defaultVisible: true },
  { id: "start_date", label: "Start date", defaultVisible: true },
  { id: "end_date", label: "End date", defaultVisible: true },
  { id: "renewal_status", label: "Status", defaultVisible: true },
  { id: "renewal_verified", label: "Renewal verified", defaultVisible: true },
  { id: "payment_verified", label: "Payment verified", defaultVisible: true },
  { id: "submitted_at", label: "Submitted", defaultVisible: true },
  { id: "actions", label: "Actions", defaultVisible: true, isAction: true },
];

const COLUMN_DEFS_FOR_FILTERS = COLUMN_DEFS.filter((c) => !c.isAction);
const SORTABLE_RENEWAL_COLUMNS = new Set([
  "renewal_code",
  "student_name",
  "amount",
  "submitted_at",
]);

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value) =>
  value === null || value === undefined
    ? "-"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(value);

const badgeClass = (isVerified) =>
  isVerified
    ? "status-badge status-badge--success"
    : "status-badge status-badge--warning";

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

export default function MembershipRenewalsDirectorySection({
  canEdit = false,
  canDelete = false,
}) {
  const [renewals, setRenewals] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortState, setSortState] = useState({
    sortBy: "submitted_at",
    sortOrder: "DESC",
  });
  const [draftUiFilters, setDraftUiFilters] = useState(defaultUiFilters);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of COLUMN_DEFS) initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    return initial;
  });
  const [selectedRenewal, setSelectedRenewal] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const visibleColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  useEffect(() => {
    setPage(1);
  }, [sortState.sortBy, sortState.sortOrder]);

  const loadRenewals = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchMembershipRenewals({
        page,
        limit: PAGE_SIZE,
        sortBy: sortState.sortBy,
        sortOrder: sortState.sortOrder,
      });
      setRenewals(result.renewals);
      setTotal(Number(result.meta?.total || 0));
      setTotalPages(Math.max(1, Number(result.meta?.totalPages || 1)));
      setPage(Number(result.meta?.page || page));
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Failed to load renewals");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [page, sortState.sortBy, sortState.sortOrder]);

  useEffect(() => {
    loadRenewals();
  }, [loadRenewals]);

  const handleSort = useCallback((column) => {
    setSortState((prev) => ({
      sortBy: column,
      sortOrder:
        prev.sortBy === column && prev.sortOrder === "ASC" ? "DESC" : "ASC",
    }));
  }, []);

  const valueFilterOptions = useMemo(() => {
    if (!draftUiFilters.column) return [];
    const seen = new Set();
    const opts = [];
    for (const row of renewals) {
      let value;
      if (
        draftUiFilters.column === "renewal_verified" ||
        draftUiFilters.column === "payment_verified"
      ) {
        value = row[draftUiFilters.column] ? "Verified" : "Not verified";
      } else {
        const raw = row[draftUiFilters.column];
        if (raw == null || raw === "") continue;
        value = String(raw);
      }
      if (!seen.has(value)) {
        seen.add(value);
        opts.push({ value, label: value });
      }
    }
    return opts;
  }, [draftUiFilters.column, renewals]);

  const filteredRenewals = useMemo(() => {
    const search = String(draftUiFilters.student_search || "")
      .trim()
      .toLowerCase();
    const col = draftUiFilters.column;
    const val = String(draftUiFilters.value || "").trim();
    return renewals.filter((row) => {
      if (search) {
        const target = String(row.student_name || "").toLowerCase();
        if (!target.includes(search)) return false;
      }
      if (col && val) {
        if (col === "renewal_verified" || col === "payment_verified") {
          const flag = row[col] ? "Verified" : "Not verified";
          return flag === val;
        }
        return String(row[col] ?? "") === val;
      }
      return true;
    });
  }, [renewals, draftUiFilters]);

  const getRenewalsExportData = useCallback(() => {
    const exportColumns = visibleColumns.filter(
      (c) => c.id !== "select" && c.id !== "actions",
    );
    const headers = exportColumns.map((c) => c.label);
    const rows = filteredRenewals.map((r) =>
      exportColumns.map((c) => {
        if (c.id === "renewal_verified")
          return r.renewal_verified ? "Verified" : "Not verified";
        if (c.id === "payment_verified")
          return r.payment_verified ? "Verified" : "Not verified";
        if (c.id === "start_date") return formatDate(r.start_date);
        if (c.id === "end_date") return formatDate(r.end_date);
        if (c.id === "submitted_at") return formatDate(r.submitted_at);
        if (c.id === "amount") return String(r.amount ?? "");
        return r[c.id] ?? "";
      }),
    );
    return { headers, rows };
  }, [visibleColumns, filteredRenewals]);

  const getCsrf = async () => {
    let csrf = localStorage.getItem("csrf-token") || localStorage.getItem("csrf_token");
    if (!csrf) {
      csrf = await getCsrfToken();
      localStorage.setItem("csrf-token", csrf);
    }
    return csrf;
  };

  const toggleSelectAll = useCallback(() => {
    if (filteredRenewals.length === 0) return;
    const allIds = new Set(filteredRenewals.map((x) => x.renewal_id));
    const allSelected = [...allIds].every((id) => selectedIds.has(id));
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(allIds);
  }, [filteredRenewals, selectedIds]);

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulkRenewalVerify = async (verified) => {
    if (!selectedIds.size) return;
    const selectedRows = filteredRenewals.filter((r) => selectedIds.has(r.renewal_id));
    if (verified) {
      const blocked = selectedRows.filter((r) => !r.application_verified);
      if (blocked.length === selectedRows.length) {
        setError(
          "Cannot approve renewals until the linked membership application is verified.",
        );
        return;
      }
    }
    setProcessing(true);
    setError("");
    setSuccess("");
    try {
      const csrf = await getCsrf();
      const ids = verified
        ? selectedRows.filter((r) => r.application_verified).map((r) => r.renewal_id)
        : selectedRows.map((r) => r.renewal_id);
      const results = await Promise.allSettled(
        ids.map((id) => verifyRenewal(id, { verified }, csrf)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const ok = results.length - failed;
      if (ok === 0) {
        setError(
          verified
            ? "No renewals were approved. Verify the membership application first."
            : "Failed to update selected renewals.",
        );
      } else {
        const skippedApp = verified ? selectedRows.length - ids.length : 0;
        setSuccess(
          verified
            ? `Approved ${ok} renewal(s).${skippedApp ? ` ${skippedApp} skipped (application not verified).` : ""}${failed ? ` ${failed} failed.` : ""}`
            : `Updated ${ok} renewal(s).${failed ? ` ${failed} failed.` : ""}`,
        );
      }
      await loadRenewals();
    } catch (err) {
      setError(err.message || "Failed to update renewals");
    } finally {
      setProcessing(false);
    }
  };

  const onDelete = async (id) => {
    setProcessing(true);
    setError("");
    try {
      const csrf = await getCsrf();
      await deleteRenewal(id, csrf);
      setSuccess("Renewal deleted successfully");
      await loadRenewals();
    } catch (err) {
      setError(err.message || "Failed to delete renewal");
    } finally {
      setProcessing(false);
    }
  };

  const onView = async (id) => {
    setLoadingDetails(true);
    setError("");
    try {
      const data = await fetchRenewalById(id);
      setSelectedRenewal(data);
      setShowDetailModal(true);
    } catch (err) {
      setError(err.message || "Failed to load renewal details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const selectedCount = selectedIds.size;
  const hasSelection = selectedCount > 0;

  const renewalsTableHeaders = useMemo(
    () =>
      visibleColumns.map((col) => {
        if (col.id === "select") {
          return {
            key: col.id,
            className: "checkbox-cell",
            label: (
              <input
                type="checkbox"
                onChange={toggleSelectAll}
                checked={
                  filteredRenewals.length > 0 &&
                  filteredRenewals.every((r) => selectedIds.has(r.renewal_id))
                }
                ref={(el) => {
                  if (el) {
                    el.indeterminate =
                      selectedIds.size > 0 &&
                      selectedIds.size < filteredRenewals.length;
                  }
                }}
                disabled={filteredRenewals.length === 0}
                aria-label="Select all renewals"
              />
            ),
          };
        }
        if (col.id === "actions") {
          return { key: col.id, label: "Actions" };
        }
        if (SORTABLE_RENEWAL_COLUMNS.has(col.id)) {
          return {
            key: col.id,
            label: (
              <button
                type="button"
                className="sortable"
                onClick={() => handleSort(col.id)}
                style={{
                  margin: 0,
                  padding: 0,
                  font: "inherit",
                  fontWeight: 600,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {col.label}
                {sortState.sortBy === col.id ? (
                  <span>{sortState.sortOrder === "ASC" ? " ↑" : " ↓"}</span>
                ) : null}
              </button>
            ),
          };
        }
        return { key: col.id, label: col.label };
      }),
    [
      visibleColumns,
      filteredRenewals,
      selectedIds,
      sortState.sortBy,
      sortState.sortOrder,
      toggleSelectAll,
      handleSort,
    ],
  );

  if (initialLoading) {
    return (
      <div
        id="ms-renewals"
        className="user-builder-panel"
        style={{ scrollMarginTop: "2rem" }}
      >
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Membership Renewals</h3>
        </div>
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading renewals...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="ms-renewals"
      className="user-builder-panel"
      style={{ scrollMarginTop: "2rem" }}
    >
      <div className="user-builder-panel__head" style={{ marginBottom: "0.35rem" }}>
        <h3 className="user-builder-panel__title">Membership Renewals</h3>
        <p className="user-builder-panel__hint">
          Review and process membership renewals submitted by volunteers. Approve a renewal only
          after its membership application is verified.
        </p>
      </div>

      <GlobalSuccessFlashModal message={success} onDismiss={() => setSuccess("")} />
      <GlobalFeedback type="error" text={error} />

      <GlobalDirectoryShell
        filters={
          <GlobalDirectoryFiltersSection
            className="ub-filters"
            searchLabel="Search by student name"
            searchPlaceholder="Type student name"
            searchValue={draftUiFilters.student_search}
            onSearchChange={(v) =>
              setDraftUiFilters((f) => ({ ...f, student_search: v }))
            }
            columnLabel="Column"
            columnValue={draftUiFilters.column}
            onColumnChange={(v) =>
              setDraftUiFilters((f) => ({ ...f, column: v, value: "" }))
            }
            gridColumnDefs={COLUMN_DEFS_FOR_FILTERS}
            valueLabel="Value"
            valueValue={draftUiFilters.value}
            onValueChange={(v) => setDraftUiFilters((f) => ({ ...f, value: v }))}
            valueOptions={valueFilterOptions}
            onClear={() => setDraftUiFilters(defaultUiFilters())}
            clearFilterLabel="Clear filter"
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
                storageKey={COLUMN_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getRenewalsExportData}
                filenameBase={`membership_renewals_${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
              />
            }
          />
        }
        approvals={
          <GlobalDirectoryApprovalsSection
            title="Approval & Marking"
            hint={
              hasSelection
                ? `${selectedCount} selected`
                : "Select checkboxes to mark in bulk"
            }
            actions={
              <>
                {canEdit ? (
                  <>
                <GlobalButton
                  variant="ghost"
                  size="sm"
                  disabled={!hasSelection || processing}
                  onClick={() => runBulkRenewalVerify(true)}
                >
                  Verify Renewal
                </GlobalButton>
                <GlobalButton
                  variant="ghost"
                  size="sm"
                  disabled={!hasSelection || processing}
                  onClick={() => runBulkRenewalVerify(false)}
                >
                  Unverify Renewal
                </GlobalButton>
                  </>
                ) : null}
                <GlobalButton
                  variant="secondary"
                  size="sm"
                  disabled={!hasSelection}
                  onClick={() => setSelectedIds(new Set())}
                >
                  Clear Selection
                </GlobalButton>
              </>
            }
          />
        }
        grid={
          <GlobalDirectoryGridSection
            loading={loading && renewals.length === 0}
            loadingMessage="Loading renewals…"
            headers={renewalsTableHeaders}
            isEmpty={!loading && filteredRenewals.length === 0}
            emptyMessage="No renewals found"
            tableAriaLabel="Membership renewals"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {filteredRenewals.map((row) => (
              <tr key={row.renewal_id}>
                {columnVisibility.select && (
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.renewal_id)}
                      onChange={() => toggleSelectOne(row.renewal_id)}
                    />
                  </td>
                )}
                {columnVisibility.renewal_code && <td>{row.renewal_code}</td>}
                {columnVisibility.student_name && (
                  <td>
                    <div>{row.student_name || "-"}</div>
                    <small style={{ color: "#64748b" }}>{row.student_code || "-"}</small>
                  </td>
                )}
                {columnVisibility.plan_name && <td>{row.plan_name}</td>}
                {columnVisibility.amount && <td>{formatCurrency(row.amount)}</td>}
                {columnVisibility.start_date && <td>{formatDate(row.start_date)}</td>}
                {columnVisibility.end_date && <td>{formatDate(row.end_date)}</td>}
                {columnVisibility.renewal_status && (
                  <td>
                    <span className="status-badge">{row.renewal_status || "-"}</span>
                  </td>
                )}
                {columnVisibility.renewal_verified && (
                  <td>
                    <span className={badgeClass(Boolean(row.renewal_verified))}>
                      {row.renewal_verified ? "Verified" : "Not verified"}
                    </span>
                  </td>
                )}
                {columnVisibility.payment_verified && (
                  <td>
                    <span className={badgeClass(Boolean(row.payment_verified))}>
                      {row.payment_verified ? "Verified" : "Not verified"}
                    </span>
                  </td>
                )}
                {columnVisibility.submitted_at && (
                  <td>{formatDate(row.submitted_at)}</td>
                )}
                {columnVisibility.actions && (
                  <td className="ub-table__col-actions">
                    <div className="ub-table-actions">
                      <GlobalTableActionButton
                        action="view"
                        onClick={() => onView(row.renewal_id)}
                        disabled={processing || loadingDetails}
                      />
                      {canDelete ? (
                        <GlobalTableActionButton
                          action="delete"
                          onClick={() => onDelete(row.renewal_id)}
                          disabled={processing}
                        />
                      ) : null}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </GlobalDirectoryGridSection>
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setPage(p)}
            label="Membership renewals pagination"
          />
        }
      />

      <GlobalModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Renewal Details"
        size="large"
      >
        {selectedRenewal ? (
          <div className="application-details">
            <div className="detail-section">
              <h4>Renewal Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Renewal Code</label>
                  <span>{selectedRenewal.renewal_code || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Submitted At</label>
                  <span>{formatDate(selectedRenewal.submitted_at)}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className="status-badge">{selectedRenewal.renewal_status || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Duration</label>
                  <span>{selectedRenewal.duration_name || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Renewal Verified</label>
                  <span className={badgeClass(Boolean(selectedRenewal.renewal_verified))}>
                    {selectedRenewal.renewal_verified ? "Verified" : "Not verified"}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Payment Verified</label>
                  <span className={badgeClass(Boolean(selectedRenewal.payment_verified))}>
                    {selectedRenewal.payment_verified ? "Verified" : "Not verified"}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Start Date</label>
                  <span>{formatDate(selectedRenewal.start_date)}</span>
                </div>
                <div className="detail-item">
                  <label>End Date</label>
                  <span>{formatDate(selectedRenewal.end_date)}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Student Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <span>{selectedRenewal.student_name || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Student Code</label>
                  <span>{selectedRenewal.student_code || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <span>{selectedRenewal.student_phone || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <span>{selectedRenewal.student_email || "-"}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Plan & Payment</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Plan Name</label>
                  <span>{selectedRenewal.plan_name || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Amount</label>
                  <span>{formatCurrency(selectedRenewal.amount)}</span>
                </div>
                <div className="detail-item">
                  <label>Receipt No</label>
                  <span>{selectedRenewal.receipt_no || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Transaction ID</label>
                  <span>{selectedRenewal.transaction_id || "-"}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Remarks</h4>
              <p className="remarks-text">{selectedRenewal.remarks || "-"}</p>
            </div>
          </div>
        ) : (
          <p>No details available.</p>
        )}
      </GlobalModal>
    </div>
  );
}
